/**
 * useAuthStore — central Pinia store for JWT + HttpOnly-refresh-cookie auth.
 *
 * Access tokens are kept in-memory only (never localStorage).
 * The HttpOnly refresh cookie is sent automatically by the browser;
 * we persist the tenant slug and tenant id (non-sensitive) so token refresh
 * survives a page reload.
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authClient } from '@/api/authClient.js'
import { saveOrganisation, stageOrganisationChoice, consumeOrganisationChoice } from '@/composables/rememberedOrganisation.js'

export const useAuthStore = defineStore('auth', () => {
  // ── State ────────────────────────────────────────────────────────────────
  const accessToken = ref(null)
  const tokenExpiresAt = ref(null)
  const user = ref(null)
  const currentTenant = ref(null)
  const authLevel = ref(null)
  const availableTenants = ref([])
  const isRefreshing = ref(false)
  const pendingStepUp = ref(null) // { step_up_required, required_aal, tenant_slug, methods }

  /** Permissions payload embedded in every token response */
  const permissions = ref(null)
  /** Feature flags dict: { feature_key: bool } */
  const featureFlags = ref({})

  // ── Getters ──────────────────────────────────────────────────────────────
  const isExpired = computed(() => {
    if (!tokenExpiresAt.value) return true
    return Date.now() / 1000 > tokenExpiresAt.value - 30 // 30-second buffer
  })

  const isAuthenticated = computed(() => !!accessToken.value && !isExpired.value)

  // ── Internal helpers ─────────────────────────────────────────────────────
  let refreshTimer = null

  function scheduleRefresh(expiresIn) {
    if (refreshTimer) clearTimeout(refreshTimer)
    const delay = Math.max(0, (expiresIn - 60) * 1000)
    refreshTimer = setTimeout(() => {
      refreshToken().catch(() => {})
    }, delay)
  }

  function setTokens(response) {
    accessToken.value = response.access
    tokenExpiresAt.value = Math.floor(Date.now() / 1000) + response.expires_in
    if (response.user) user.value = response.user
    // Support both 'tenant' (login/token-exchange) and 'current_tenant' (switch)
    if (response.tenant) currentTenant.value = response.tenant
    if (response.current_tenant) currentTenant.value = response.current_tenant
    if (response.auth_level != null) authLevel.value = response.auth_level
    if (response.available_tenants) availableTenants.value = response.available_tenants
    // Permissions + feature flags embedded in every token response
    if (response.permissions) permissions.value = response.permissions
    if (response.feature_flags) featureFlags.value = response.feature_flags
    // Persist non-sensitive tenant identity for silent refresh after page reload.
    try {
      if (currentTenant.value?.slug) {
        localStorage.setItem('current_tenant_slug', currentTenant.value.slug)
      }
    } catch { /* ignore */ }
    scheduleRefresh(response.expires_in)
  }

  function clearAll() {
    accessToken.value = null
    tokenExpiresAt.value = null
    user.value = null
    currentTenant.value = null
    authLevel.value = null
    availableTenants.value = []
    permissions.value = null
    featureFlags.value = {}
    isRefreshing.value = false
    pendingStepUp.value = null
    if (refreshTimer) { clearTimeout(refreshTimer); refreshTimer = null }
    try { localStorage.removeItem('current_tenant_slug') } catch { /* ignore */ }
    // Optional remembered organisation is controlled separately from this session.
  }

  function setPendingStepUp(data) {
    pendingStepUp.value = data
  }

  // ── Public actions ────────────────────────────────────────────────────────

  async function discoverEmail(email) {
    const { data } = await authClient.post('auth/discover/', { email })
    return data
  }

  async function _fetchAndSetAvailableTenants() {
    if (!accessToken.value) return
    try {
      const tenantsResp = await authClient.get('auth/tenants/', {
        headers: {
          Authorization: `Bearer ${accessToken.value}`,
          ...(currentTenant.value?.slug ? { 'X-Tenant-Slug': currentTenant.value.slug } : {}),
        },
      })
      const data = tenantsResp.data
      const list = data?.results ?? data?.tenants ?? data ?? []
      if (Array.isArray(list) && list.length) availableTenants.value = list
    } catch { /* non-fatal */ }
  }

  async function loginWithPassword(email, password, tenantSlug, rememberOrganisation = false) {
    const body = { email, password }
    if (tenantSlug) body.tenant_slug = tenantSlug
    const { data } = await authClient.post('auth/login/', body)
    if (data.access) {
      setTokens(data)
      saveOrganisation(currentTenant.value, rememberOrganisation)
      await _fetchAndSetAvailableTenants()
    }
    return data
  }

  async function loginWithOIDC(tenantSlug, providerId, rememberOrganisation = null) {
    const params = { tenant_slug: tenantSlug }
    if (providerId != null) params.provider_id = providerId
    const { data } = await authClient.get('auth/oidc/authorize/', { params })
    if (data.authorization_url) {
      stageOrganisationChoice(data.authorization_url, rememberOrganisation)
      window.location.href = data.authorization_url
    }
    return data
  }

  async function handleOIDCCallback(code, state) {
    const rememberOrganisation = consumeOrganisationChoice(state)
    const { data } = await authClient.get('auth/oidc/callback/', { params: { code, state } })
    if (data.access) {
      setTokens(data)
      if (rememberOrganisation !== null) saveOrganisation(currentTenant.value, rememberOrganisation)
    }
    return data
  }

  // Mutex: prevents concurrent refresh calls when multiple requests get 401
  let refreshPromise = null

  async function refreshToken(tenantSlug) {
    if (refreshPromise) return refreshPromise
    refreshPromise = _doRefresh(tenantSlug).finally(() => { refreshPromise = null })
    return refreshPromise
  }

  async function _doRefresh(tenantSlug) {
    isRefreshing.value = true
    try {
      let slug = tenantSlug ?? currentTenant.value?.slug
      if (!slug) {
        try { slug = localStorage.getItem('current_tenant_slug') || undefined } catch { /* ignore */ }
      }
      const body = slug ? { tenant_slug: slug } : {}
      const { data } = await authClient.post('auth/token/', body)
      setTokens(data)
      // The refresh response doesn't include available_tenants — fetch separately.
      await _fetchAndSetAvailableTenants()
      return data
    } finally {
      isRefreshing.value = false
    }
  }

  async function switchTenant(tenantSlug) {
    const headers = {}
    if (accessToken.value) headers['Authorization'] = `Bearer ${accessToken.value}`
    if (currentTenant.value?.slug) headers['X-Tenant-Slug'] = currentTenant.value.slug
    const { data } = await authClient.post(
      'auth/tenant/switch/',
      { tenant_slug: tenantSlug },
      { headers },
    )
    if (data.access) {
      setTokens(data)
    }
    return data
  }

  async function logout(all = false) {
    const token = accessToken.value
    const tenantSlug = currentTenant.value?.slug
    // Clear state immediately — don't wait for revoke
    clearAll()
    if (token) {
      try {
        const body = all ? { all: true } : {}
        await authClient.post('auth/token/revoke/', body, {
          headers: {
            Authorization: `Bearer ${token}`,
            ...(tenantSlug ? { 'X-Tenant-Slug': tenantSlug } : {}),
          },
        })
      } catch { /* fire-and-forget */ }
    }
  }

  return {
    // State (reactive refs)
    accessToken, tokenExpiresAt, user, currentTenant, authLevel, availableTenants,
    isRefreshing, pendingStepUp, permissions, featureFlags,
    // Getters
    isAuthenticated, isExpired,
    // Actions
    discoverEmail, loginWithPassword, loginWithOIDC, handleOIDCCallback,
    refreshToken, switchTenant, logout, setTokens, clearAll, setPendingStepUp,
  }
})
